/**
 * useStaffRole — reads the cached staff-user JSON from localStorage and
 * returns the active role with convenience booleans for the most common
 * role gates.
 *
 * Naming note: the "Real Estate Desk" role is stored as the legacy key
 * `contract_desk` in the database (column users.role). The UI label was
 * relabeled to "Real Estate Desk" without renaming the underlying key,
 * to avoid a destructive UPDATE on the production users table. New code
 * should use `isREDesk` (this hook) rather than string-comparing the
 * raw role value, so the eventual rename is a one-line change here.
 *
 * The doorly_admin_user JSON is written at login by AdminLogin.jsx and
 * kept in sync by EditProfile via window.dispatchEvent('doorly:profileUpdated').
 */
export function useStaffRole() {
  try {
    const user = JSON.parse(localStorage.getItem('doorly_admin_user') || '{}');
    // 🚨 No role is no role (hotfix-priv, 2026-11-01): the server sends 'unassigned' and refuses every gate. Display only.
    const role = user.role || 'unassigned';
    return {
      role,
      isAdmin:   role === 'admin',
      isREDesk:  role === 'contract_desk',
      isLO:      role === 'loan_officer',
      // Partner Success — PARTNER-side only. This boolean is for hiding UI that
      // would 403 anyway; the server-side guard (constants/staffRoles.js) is what
      // actually denies borrower data, and it does so independently of this.
      isPartnerSuccess: role === 'partner_success',
      staffName: `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim(),
      staffId:   user.id || null,
    };
  } catch {
    // Corrupt localStorage — fail safe to "admin" so a broken cache
    // doesn't lock the user out of their own portal. The backend gate
    // is still authoritative.
    return { role: 'admin', isAdmin: true, isREDesk: false, isLO: false, isPartnerSuccess: false, staffName: '', staffId: null };
  }
}
